import {
  Component,
  OnInit,
  OnDestroy,
  EventEmitter,
  Output,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { finalize, Subject, Subscription, takeUntil } from 'rxjs';
import { GtwElementsBaseUrlsModel } from '../../../../shared/_models/gtw-elements-base-urls.model';
import { GlobalService } from '../../../../global/services/global.service';
import { WorkspaceService } from '../../../../workspace/workspace.service';
import { AppService } from '../../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
  hydratedGlobalParams,
} from '../../../../shared/workspace-element-context';

import {
  Classification,
  LegalEntity,
  OverrideEntity,
  TaxConsolidationGroup,
  RETURN_PREPARED_BY_OPTIONS,
  ENTITY_TYPE_OPTIONS,
  YES_NO_OPTIONS,
} from '../models/entity-classification.models';
import { EntityClassificationActionsService } from '../services/entity-classification-actions.service';
import { GtwSnackbarService } from 'projects/gtw-elements/src/app/shared/_services/gtw-snackbar.service';

@Component({
    selector: 'app-entity-classification-create',
    templateUrl: './entity-classification-create.component.html',
    styleUrls: ['./entity-classification-create.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class EntityClassificationCreateComponent implements OnInit, OnDestroy {
  private readonly subs = new Subscription();
  // Header data
  header_data: any = {};
  globalService: any;
  USER_SETTINGS: any;
  SERVER_CONFIG: any;
  GENERAL_CONFIG: any;

  // Filter parameters
  filterParams: any;
  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  // Base URLs configuration
  baseURLs: GtwElementsBaseUrlsModel = {
    admin: '',
    api: '',
    custom: '',
    efile: '',
    pdfEngine: '',
    bulkPdfEngine: '',
  };

  @Output() closeModal = new EventEmitter<void>();
  @Output() entitySaved = new EventEmitter<OverrideEntity>();

  // Form and UI state
  entityForm: UntypedFormGroup;
  modalTitle = 'Create New Override Entity';
  showEntityLoader = true;
  minimized = false;

  // Loading states
  isSaving = false;

  // Data arrays
  allEntities: LegalEntity[] = [];
  allMasterLeids: OverrideEntity[] = [];
  allClassifications: Classification[] = [];
  allTaxConsolidationGroups: TaxConsolidationGroup[] = [];

  // API completion flags
  entitiesApiCompleted = false;
  classificationsApiCompleted = false;
  taxGroupsApiCompleted = false;
  private isLoadingData = false;

  private overrideEntityBackup: Partial<OverrideEntity> | null = null;

  // Constants for template
  readonly RETURN_PREPARED_BY_OPTIONS = RETURN_PREPARED_BY_OPTIONS;
  readonly ENTITY_TYPE_OPTIONS = ENTITY_TYPE_OPTIONS;
  readonly YES_NO_OPTIONS = YES_NO_OPTIONS;

  private destroy$ = new Subject<void>();

  constructor(
    private entityService: EntityClassificationActionsService,
    private gtwSnackbarService: GtwSnackbarService,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) {
    this.entityForm = this.entityService.createEntityForm();
    this.overrideEntityBackup = this.entityService.getDefaultEntity();
  }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.initializeService();
      this.loadInitialData();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.initializeService();
      this.loadInitialData();
    }));
    this.setupFormSubscriptions();
    this.initializeService();
    this.loadInitialData();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    header.globalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || header.globalParams || {};
    const gc = generalConfigFromGlobal(this.gtwGlobal);
    this.baseURLs = {
      admin: gc.admin ?? '',
      api: gc.api ?? gc.base_url ?? '',
      custom: gc.custom ?? '',
      efile: gc.efile ?? '',
      pdfEngine: gc.pdfEngine ?? '',
      bulkPdfEngine: gc.bulkPdfEngine ?? '',
    };
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.globalService = header;
    this.USER_SETTINGS = header.userSettingsData || {};
    this.SERVER_CONFIG = header.serverConfigData || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    this.cdr.markForCheck();
  }

  private getApiBaseUrl(): string {
    return this.baseURLs?.api || this.GENERAL_CONFIG?.base_url || '';
  }

  /**
   * Initialize service with base URLs and parameters
   */
  private initializeService(): void {
    if (
      this.baseURLs?.api &&
      this.baseURLs?.api != '' &&
      this.globalService?.globalParams &&
      this.filterParams
    ) {
      console.log('Initializing service with:', {
        baseURLs: this.baseURLs,
        globalParams: this.globalService.globalParams,
        filterParams: this.filterParams,
      });

      // Validate critical configuration
      if (!this.baseURLs?.api) {
        console.error(
          'Service initialization warning: API base URL is missing'
        );
      }

      if (!this.filterParams?.scenario) {
        console.error(
          'Service initialization warning: Scenario is missing from filter params'
        );
      }

      if (!this.globalService?.globalParams) {
        console.error(
          'Service initialization warning: Global params is missing'
        );
      }

      console.log('Service initialized successfully');
    }
  }

  /**
   * Set up form field subscriptions for reactive behavior
   */
  private setupFormSubscriptions(): void {
    // Watch tax consolidation group changes
    this.entityForm
      .get('TAX_CONSOLIDATION_GRP')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((taxGroup) => {
        if (taxGroup) {
          this.entityService.handleTaxConsolidationGroupChange(
            this.entityForm,
            taxGroup,
            this.allTaxConsolidationGroups
          );
          this.cdr.markForCheck();
        }
      });

    // Watch classification changes
    this.entityForm
      .get('CLASSIFICATION')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((classification) => {
        if (classification) {
          this.entityService.handleClassificationChange(
            this.entityForm,
            classification,
            this.allClassifications
          );
          this.cdr.markForCheck();
        }
      });
  }

  /**
   * Load initial data
   */
  private loadInitialData(): void {
    const apiBase = this.getApiBaseUrl();
    if (!apiBase || !this.globalService?.globalParams) {
      this.showEntityLoader = false;
      this.cdr.markForCheck();
      return;
    }

    if (this.isLoadingData) {
      return;
    }

    this.isLoadingData = true;
    this.entitiesApiCompleted = false;
    this.classificationsApiCompleted = false;
    this.taxGroupsApiCompleted = false;
    this.showEntityLoader = true;
    this.cdr.markForCheck();

    this.loadAllEntities();
    this.loadAllOverrideEntities();
    this.loadClassifications();
    this.loadTaxConsolidationGroups();
  }

  private loadAllOverrideEntities(): void {
    this.entityService
      .loadAllOverrideEntities(
        this.getApiBaseUrl(),
        this.globalService?.globalParams ?? {},
        this.filterParams
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (entities: OverrideEntity[]) => {
          this.allMasterLeids = entities;
          this.cdr.markForCheck();
        },
        error: (error: any) => {
          console.error('Error loading override entities:', error);
          this.cdr.markForCheck();
        },
      });
  }

  /**
   * Load all entities for dropdown
   */
  private loadAllEntities(): void {
    this.entityService
      .loadAllEntities(
        this.getApiBaseUrl(),
        this.globalService?.globalParams ?? {},
        this.filterParams
      )
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.checkLoadingCompletion())
      )
      .subscribe({
        next: (entities: LegalEntity[]) => {
          this.allEntities = entities;
          this.entitiesApiCompleted = true;
          this.cdr.markForCheck();
        },
        error: (error: any) => {
          console.error('Error loading entities:', error);
          this.entitiesApiCompleted = true;
          this.cdr.markForCheck();
        },
      });
  }

  /**
   * Load classifications
   */
  private loadClassifications(): void {
    this.entityService
      .loadClassifications(
        this.getApiBaseUrl(),
        this.globalService?.globalParams ?? {},
        this.filterParams
      )
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.checkLoadingCompletion())
      )
      .subscribe({
        next: (classifications: Classification[]) => {
          this.allClassifications = classifications;
          this.classificationsApiCompleted = true;
          this.cdr.markForCheck();
        },
        error: (error: any) => {
          console.error('Error loading classifications:', error);
          this.classificationsApiCompleted = true;
          this.cdr.markForCheck();
        },
      });
  }

  /**
   * Load tax consolidation groups
   */
  private loadTaxConsolidationGroups(): void {
    this.entityService
      .loadTaxConsolidationGroups(
        this.getApiBaseUrl(),
        this.globalService?.globalParams ?? {},
        this.filterParams
      )
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.checkLoadingCompletion())
      )
      .subscribe({
        next: (groups: TaxConsolidationGroup[]) => {
          this.allTaxConsolidationGroups = groups;
          this.taxGroupsApiCompleted = true;
          this.cdr.markForCheck();
        },
        error: (error: any) => {
          console.error('Error loading tax consolidation groups:', error);
          this.taxGroupsApiCompleted = true;
          this.cdr.markForCheck();
        },
      });
  }

  /**
   * Check if all API calls are completed
   */
  private checkLoadingCompletion(): void {
    if (
      this.entitiesApiCompleted &&
      this.classificationsApiCompleted &&
      this.taxGroupsApiCompleted
    ) {
      this.showEntityLoader = false;
      this.isLoadingData = false;
      this.cdr.markForCheck();
    }
  }

  /**
   * Legacy onLeidChange — populate from master override list on blur.
   */
  onLeidChange(): void {
    const childId = this.entityForm.get('CHILD_ID')?.value;
    if (!childId) {
      return;
    }

    const selectedElem = this.allMasterLeids.find((e) => e.CHILD_ID === childId);
    if (!selectedElem || selectedElem.CHILD_ID !== childId) {
      return;
    }

    this.entityService.populateFormWithEntity(
      this.entityForm,
      this.entityService.normalizeMasterLeidEntity(selectedElem)
    );
    this.cdr.markForCheck();
  }

  /**
   * Handle reporting period selection change (legacy onReportingSelection).
   */
  onReportingPeriodChange(event: Event): void {
    const stub = (event.target as HTMLSelectElement)?.value;
    if (stub == null || stub === '') {
      return;
    }

    const childId = this.entityForm.get('CHILD_ID')?.value;
    const selectedElem = this.allMasterLeids.find(
      (e) => e.CHILD_ID === childId && String(e.STUB) === String(stub)
    );

    if (selectedElem) {
      this.entityService.populateFormWithEntity(
        this.entityForm,
        this.entityService.normalizeMasterLeidEntity(selectedElem)
      );
    } else {
      const defaults = this.entityService.getDefaultEntity();
      this.entityForm.patchValue({
        ...defaults,
        CHILD_ID: childId,
        STUB: stub,
      });
    }
    this.cdr.markForCheck();
  }

  /**
   * Save entity
   */
  save(): void {
    if (!this.entityService.validateForm(this.entityForm)) {
      this.gtwSnackbarService.openSnackBar(
        'Please fill all the required fields',
        'gtw-snackbar--yellow'
      );
      this.cdr.markForCheck();
      return;
    }

    this.isSaving = true;

    const formValue = this.entityForm.getRawValue();

    // Set scenario from filter params
    formValue.SCENARIO = this.filterParams?.scenario;

    // Prepare entity with descriptions
    const entity = this.entityService.prepareEntityForSave(
      formValue,
      this.allClassifications,
      this.allTaxConsolidationGroups
    );

    this.entityService
      .saveEntity(this.getApiBaseUrl(), entity, this.globalService?.globalParams ?? {}, this.filterParams)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isSaving = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: (response: { callSuccess: string; errorMessage: string }) => {
          if (
            response.callSuccess === '1' &&
            response.errorMessage === 'null'
          ) {
            this.gtwSnackbarService.openSnackBar(
              'Override entity created successfully',
              'gtw-snackbar--green'
            );
            this.requestGridUpdate.emit('refresh');
          } else {
            // Show error message could be implemented here
            this.gtwSnackbarService.openSnackBar(
              response.errorMessage,
              'gtw-snackbar--red'
            );
            console.error('Save error:', response.errorMessage);
          }
        },
        error: (error: any) => {
          this.gtwSnackbarService.openSnackBar(
            'Error occurred while saving. Please try again.',
            'gtw-snackbar--red'
          );
          // Show error message could be implemented here
          console.error('Save error:', error);
        },
      });
  }

  /**
   * Reset form
   */
  reset(): void {
    if (this.overrideEntityBackup) {
      this.entityForm.reset({ ...this.overrideEntityBackup });
      this.cdr.markForCheck();
    }
  }

  /**
   * Cancel and close modal
   */
  cancel(): void {
    const updateEvent = new CustomEvent('request-grid-update', {
      detail: 'cancel',
      bubbles: true,
    });

    if (document.querySelector('gtw-entity-classification-create-ce')) {
      document
        .querySelector('gtw-entity-classification-create-ce')
        ?.dispatchEvent(updateEvent);
    }
  }

  /**
   * Toggle modal minimized state
   */
  toggleMinimized(): void {
    this.minimized = !this.minimized;
    this.cdr.markForCheck();
  }

  /**
   * Get field error message
   */
  getFieldError(fieldName: string): string | null {
    return this.entityService.getFieldError(this.entityForm, fieldName);
  }

  /**
   * Check if field has error
   */
  hasFieldError(fieldName: string): boolean {
    return this.entityService.hasFieldError(this.entityForm, fieldName);
  }

  /**
   * Get available reporting periods
   */
  get reportingPeriods() {
    return this.entityService.getReportingPeriods();
  }

  /**
   * Track by function for ngFor performance
   */
  trackByIndex(index: number, item: any): number {
    return this.entityService.trackByIndex(index, item);
  }

  /**
   * Track by entity ID for ngFor performance
   */
  trackByEntityId(index: number, entity: LegalEntity): string {
    return this.entityService.trackByEntityId(index, entity);
  }
}
